import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { codeColorMap, codeColorMapHover } from 'lib/colors';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';
import { actions } from '../Chart.redux';

const defaultSymbols = [];

const AllSymbolsDialog = () => {
    const dispatch = useDispatch();
    const showDialog = useSelector((state) => state.chart.toolbar.showAllSymbolsDialog);
    const symbols = useSelector((state) => state.chart.symbols) || defaultSymbols;
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    const handleClose = () => dispatch(actions.setToolbar({ showAllSymbolsDialog: false }));

    const handleSelection = useCallback(
        (row) => {
            dispatch(
                actions.setToolbar({
                    selectedButton: 'symbols',
                    selectedTool: 'symbolSingle',
                })
            );

            dispatch(
                actions.resetSymbolOptions({
                    symbol: row.symbol_id,
                })
            );

            handleClose();
        },
        [dispatch]
    );

    const columns = useMemo(
        () => [
            {
                field: 'symbol_abbreviation',
                headerName: 'Abbreviation',
                width: 120,
                sortable: false,
                renderCell: (params) => {
                    const category = params.row.symbol_category;
                    const color = codeColorMap[category] ?? codeColorMap.other;
                    const hoverColor = codeColorMapHover[category] ?? codeColorMapHover.other;

                    return (
                        <Box
                            onClick={(event) => {
                                event.stopPropagation();
                                handleSelection(params.row);
                            }}
                            sx={{
                                width: '100%',
                                cursor: 'pointer',
                                borderLeft: `15px solid ${color}`,
                                padding: '6px 12px',
                                background: 'linear-gradient(to left, #ffffff 50%, #ededed 50%)',
                                backgroundSize: '201% 100%',
                                backgroundPosition: 'right bottom',
                                transition: 'all 400ms ease',
                                '&:hover': {
                                    borderLeft: `15px solid ${hoverColor}`,
                                    backgroundPosition: 'left bottom',
                                },
                            }}
                        >
                            {params.value}
                        </Box>
                    );
                },
            },
            { field: 'symbol_category', headerName: 'Category', width: 160, sortable: false },
            { field: 'symbol_name', headerName: 'Name', flex: 1, minWidth: 200, sortable: false },
        ],
        [handleSelection]
    );

    const filteredSymbols = useMemo(() => {
        if (!filterModel?.items?.length) {
            return symbols;
        }

        return symbols.filter((row) => {
            return filterModel.items.every((filter) => {
                const field = filter.field;
                const value = row[field];
                const target = String(filter.value ?? '').toLowerCase();

                if (!target) {
                    return true;
                }

                return String(value ?? '')
                    .toLowerCase()
                    .includes(target);
            });
        });
    }, [filterModel, symbols]);

    return (
        <Dialog fullWidth maxWidth="lg" open={showDialog} onClose={handleClose}>
            <DialogTitle>All Codes</DialogTitle>

            <DialogContent>
                <DataGrid
                    rows={filteredSymbols}
                    columns={columns}
                    filterMode="client"
                    filterModel={filterModel}
                    onFilterModelChange={(model) => setFilterModel(model)}
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[10, 20, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    disableColumnMenu
                    disableCellFocus
                    slots={{ pagination: NumberedDataGridPagination }}
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    autoHeight
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AllSymbolsDialog;
